import React, { useEffect, useRef, useState } from "react";
import { useDispatch } from "react-redux";
import axios from "axios";
import { Button } from "primereact/button";
import { Dialog } from "primereact/dialog";
import { InputText } from "primereact/inputtext";
import { ConfirmDialog, confirmDialog } from "primereact/confirmdialog";
import SignatureCanvas from "react-signature-canvas";
import { ref, uploadBytes, getDownloadURL } from "firebase/storage";
import { storage } from "../../firebase/firebase";
import { toast } from "react-toastify";
import { handleGetRequest } from "../../services/GetTemplate";
import { handlePostRequest } from "../../services/PostTemplate";
import { handlePutRequest } from "../../services/PutTemplate";
import { DEV } from "../../services/constants";
import styles from "./agentsManager.module.scss";

const MAX_AGENTS = 2;

const emptyForm = { name: "", idLast6: "" };

const AgentsManager = ({ userId }) => {
    const dispatch = useDispatch();
    const [agents, setAgents] = useState([]);
    const [loading, setLoading] = useState(true);
    const [dialogVisible, setDialogVisible] = useState(false);
    const [editingAgent, setEditingAgent] = useState(null);
    const [form, setForm] = useState(emptyForm);
    const [signatureDataUrl, setSignatureDataUrl] = useState(null);
    const [saving, setSaving] = useState(false);
    const signatureRef = useRef(null);

    const loadAgents = async () => {
        if (!userId) return;
        setLoading(true);
        try {
            const res = await handleGetRequest(`/users/${userId}/agents`);
            setAgents(res?.data || []);
        } catch (error) {
            toast.error("Failed to load agents");
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        loadAgents();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [userId]);

    const openAddDialog = () => {
        setEditingAgent(null);
        setForm(emptyForm);
        setSignatureDataUrl(null);
        setDialogVisible(true);
    };

    const openEditDialog = (agent) => {
        setEditingAgent(agent);
        setForm({ name: agent.name, idLast6: agent.idLast6 });
        setSignatureDataUrl(agent.signatureUrl || null);
        setDialogVisible(true);
    };

    const closeDialog = () => {
        setDialogVisible(false);
        setEditingAgent(null);
        setForm(emptyForm);
        setSignatureDataUrl(null);
    };

    const clearSignature = () => {
        if (signatureRef.current) {
            signatureRef.current.clear();
        }
        setSignatureDataUrl(null);
    };

    const saveSignature = () => {
        if (signatureRef.current && !signatureRef.current.isEmpty()) {
            setSignatureDataUrl(signatureRef.current.toDataURL());
        }
    };

    const handleIdLast6Change = (value) => {
        const digitsOnly = value.replace(/\D/g, "").slice(0, 6);
        setForm((prev) => ({ ...prev, idLast6: digitsOnly }));
    };

    const uploadSignature = async () => {
        const blob = await fetch(signatureDataUrl).then((r) => r.blob());
        const fileName = `agents/${userId}/agent_signature_${Date.now()}.png`;
        const storageRef = ref(storage, fileName);
        await uploadBytes(storageRef, blob);
        return getDownloadURL(storageRef);
    };

    const handleSave = async () => {
        if (!form.name.trim()) {
            toast.error("Agent name is required");
            return;
        }
        if (form.idLast6.length !== 6) {
            toast.error("Last 6 digits of ID must be exactly 6 digits");
            return;
        }
        if (!signatureDataUrl) {
            toast.error("Agent signature is required");
            return;
        }

        setSaving(true);
        try {
            // Reuse the already-uploaded URL if the signature wasn't redrawn during edit
            const isExistingSignature = editingAgent && signatureDataUrl === editingAgent.signatureUrl;
            const signatureUrl = isExistingSignature ? signatureDataUrl : await uploadSignature();

            if (editingAgent) {
                const res = await handlePutRequest({ name: form.name, idLast6: form.idLast6, signatureUrl }, `/users/${userId}/agents/${editingAgent._id}`);
                if (res?.success) {
                    toast.success("Agent updated");
                    setAgents(res.data);
                    closeDialog();
                }
            } else {
                const res = await dispatch(handlePostRequest({ name: form.name, idLast6: form.idLast6, signatureUrl }, `/users/${userId}/agents`, false, false));
                if (res?.success) {
                    toast.success("Agent added");
                    setAgents(res.data);
                    closeDialog();
                } else {
                    toast.error(res?.message || "Failed to add agent");
                }
            }
        } catch (error) {
            console.error("Error saving agent:", error);
            toast.error("Failed to save agent");
        } finally {
            setSaving(false);
        }
    };

    const handleDelete = (agent) => {
        confirmDialog({
            message: `Remove agent "${agent.name}"?`,
            header: "Confirm Removal",
            icon: "pi pi-exclamation-triangle",
            accept: async () => {
                try {
                    const response = await axios.delete(`${DEV}/users/${userId}/agents/${agent._id}`, {
                        headers: { Authorization: `Bearer ${localStorage.getItem("token")}` },
                    });
                    if (response?.data?.success) {
                        toast.success("Agent removed");
                        setAgents(response.data.data);
                    } else {
                        toast.error(response?.data?.message || "Failed to remove agent");
                    }
                } catch (error) {
                    console.error("Error removing agent:", error);
                    toast.error(error?.response?.data?.message || "Failed to remove agent");
                }
            },
        });
    };

    return (
        <div>
            <ConfirmDialog />
            <div className={styles.header}>
                <h3>Agents</h3>
                <Button label="Add Agent" icon="pi pi-plus" onClick={openAddDialog} disabled={loading || agents.length >= MAX_AGENTS} size="small" className={styles.addButton} />
            </div>

            {loading ? (
                <p>Loading agents...</p>
            ) : agents.length === 0 ? (
                <p className={styles.emptyState}>No agents added yet. You can add up to {MAX_AGENTS}.</p>
            ) : (
                agents.map((agent) => (
                    <div key={agent._id} className={styles.agentRow}>
                        <div className={styles.agentInfo}>
                            {agent.signatureUrl && <img src={agent.signatureUrl} alt="Signature" className={styles.signaturePreview} />}
                            <div>
                                <strong>{agent.name}</strong>
                                <p className={styles.idLine}>ID ending in {agent.idLast6}</p>
                            </div>
                        </div>
                        <div>
                            <Button icon="pi pi-pencil" className="p-button-text" onClick={() => openEditDialog(agent)} tooltip="Edit" />
                            <Button icon="pi pi-trash" className="p-button-text p-button-danger" onClick={() => handleDelete(agent)} tooltip="Remove" />
                        </div>
                    </div>
                ))
            )}

            <Dialog visible={dialogVisible} onHide={closeDialog} header={editingAgent ? "Edit Agent" : "Add Agent"} draggable={false} blockScroll style={{ width: "min(480px, 95vw)" }}>
                <div>
                    <div className={styles.field}>
                        <label>Agent Name</label>
                        <InputText value={form.name} onChange={(e) => setForm((prev) => ({ ...prev, name: e.target.value }))} />
                    </div>
                    <div className={styles.field}>
                        <label>Last 6 Digits of ID</label>
                        <InputText value={form.idLast6} onChange={(e) => handleIdLast6Change(e.target.value)} maxLength={6} />
                    </div>
                    <div className={styles.field}>
                        <label>Signature</label>
                        {signatureDataUrl ? (
                            <div>
                                <img src={signatureDataUrl} alt="Signature preview" className={styles.signaturePreviewLarge} />
                                <Button label="Redo Signature" icon="pi pi-refresh" outlined onClick={() => setSignatureDataUrl(null)} style={{ marginTop: "0.5rem" }} />
                            </div>
                        ) : (
                            <div>
                                <SignatureCanvas ref={signatureRef} canvasProps={{ className: styles.signatureCanvas }} />
                                <div style={{ display: "flex", gap: "0.5rem", marginTop: "0.5rem" }}>
                                    <Button label="Clear" icon="pi pi-times" outlined severity="danger" onClick={clearSignature} />
                                    <Button label="Save Signature" icon="pi pi-check" onClick={saveSignature} />
                                </div>
                            </div>
                        )}
                    </div>
                    <div className={styles.dialogActions}>
                        <Button label="Cancel" outlined onClick={closeDialog} disabled={saving} />
                        <Button label={saving ? "Saving..." : "Save Agent"} icon={saving ? "pi pi-spin pi-spinner" : "pi pi-check"} onClick={handleSave} disabled={saving} className={styles.saveButton} />
                    </div>
                </div>
            </Dialog>
        </div>
    );
};

export default AgentsManager;
